import { fail, ok, Result } from '../shared/Utility/result';
import { isSketchDocument, SketchDocument } from './sketch-document';

export const DRAFT_KEY_PREFIX = 'fn_sketch_draft_v1_';
export const MAX_DRAFT_BYTES = 3_000_000;
export const DRAFT_AUTOSAVE_MS = 3000;

export enum SketchMode {
  New = 'new',
  Revision = 'revision',
}

export interface StoredDraft {
  readonly savedAtIso: string;
  readonly notebookId: string;
  readonly mode: SketchMode;
  readonly noteId: string;
  readonly doc: SketchDocument;
}

export interface DraftStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export function draftKey(userId: string, notebookId: string): string {
  return `${DRAFT_KEY_PREFIX}${userId}_${notebookId}`;
}

export function saveDraft(store: DraftStore, key: string, draft: StoredDraft): Result<void> {
  const json = JSON.stringify(draft);
  if (json.length > MAX_DRAFT_BYTES) {
    return fail('The sketch is too large to keep a local backup.');
  }
  try {
    store.setItem(key, json);
    return ok(undefined);
  } catch {
    return fail('The browser refused to store a local backup.');
  }
}

export function loadDraft(store: DraftStore, key: string): StoredDraft | null {
  let raw: string | null;
  try {
    raw = store.getItem(key);
  } catch {
    return null;
  }
  if (!raw) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') {
    return null;
  }
  const draft = parsed as Record<string, unknown>;
  if (
    typeof draft['savedAtIso'] !== 'string' ||
    typeof draft['notebookId'] !== 'string' ||
    typeof draft['noteId'] !== 'string' ||
    (draft['mode'] !== SketchMode.New && draft['mode'] !== SketchMode.Revision) ||
    !isSketchDocument(draft['doc'])
  ) {
    return null;
  }
  return draft as unknown as StoredDraft;
}

export interface KeyedStore extends DraftStore {
  readonly length: number;
  key(index: number): string | null;
}

export function clearAllDrafts(store: KeyedStore): number {
  const keys: string[] = [];
  for (let i = 0; i < store.length; i++) {
    const key = store.key(i);
    if (key && key.startsWith(DRAFT_KEY_PREFIX)) {
      keys.push(key);
    }
  }
  keys.forEach((k) => clearDraft(store, k));
  return keys.length;
}

export function clearDraft(store: DraftStore, key: string): void {
  try {
    store.removeItem(key);
  } catch {
    return;
  }
}
